import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { initBugReportCapture } from './utils/bugReport';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { captureInitialVersion } from './utils/versionCheck';
// Side-effect import: attaches the beforeinstallprompt listener at startup,
// before the sidebar's install button exists to need it.
import './utils/appSetup';

const API_URL = process.env.REACT_APP_API_URL || '';

// Sentry/GlitchTip — captures uncaught frontend errors + performance traces.
// No-op when REACT_APP_SENTRY_DSN is unset, so dev without it is unaffected.
if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV,
        integrations: [Sentry.browserTracingIntegration()],
        // Performance tracing across the whole app; lower in heavy production use.
        tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 1.0),
        // Connect frontend traces to backend traces for matching API calls.
        tracePropagationTargets: [API_URL].filter(Boolean),
    });
}

initBugReportCapture();

// Baseline for UpdateNotifier's version.json diff — must run before any
// deploy could plausibly land, so as early in startup as possible.
captureInitialVersion();

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Axios response interceptor for automatic token refresh
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return axios(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Try to refresh the token
                const response = await axios.post(
                    `${API_URL}/api/auth/refresh`,
                    {},
                    { withCredentials: true }
                );

                if (response.data.success && response.data.token) {
                    const newToken = response.data.token;
                    
                    // Update the failed request with new token
                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                    
                    // Process queued requests
                    processQueue(null, newToken);
                    
                    isRefreshing = false;
                    
                    // Retry the original request
                    return axios(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed, logout user
                processQueue(refreshError, null);
                isRefreshing = false;
                
                // Clean up and redirect to login
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Set default axios config
axios.defaults.withCredentials = true;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p style={{ padding: 24 }}>Something went wrong. The team has been notified.</p>}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// The service worker is now load-bearing: public/service-worker.js is what
// receives an OS-level push notification while the app is closed / the phone
// is locked (see src/realtime/pushClient.js). It is a hand-written file, not
// a workbox precache build — there is no asset caching here, so registering
// it carries none of the "stale cached bundle" risk that usually comes with
// turning CRA's service worker on.
// onUpdate fires once the new worker has installed; without a config here
// CRA's default just console.logs it, which nobody sees. Surface it as a
// window event so UpdateNotifier can prompt for a reload instead.
serviceWorkerRegistration.register({
    onUpdate: () => {
        window.dispatchEvent(new CustomEvent('sw-update-available'));
    },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
